// Tranche AH pass 1: schedule detail page.
//
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AuditFeed,
  Button,
  DangerousActionButton,
  EntityDetailLayout,
  Icon,
  OutboxJobList,
  Spinner,
  SyncStateBadge,
  type AuditEntry,
  type OutboxJob,
  type SyncState,
} from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import {
  fetchAuditForResource,
  fetchOutboxJobsForResource,
} from '../lib/detailFetchers.js';
import { useTenantScopedPage } from '../lib/useTenantScopedPage.js';

interface ScheduleRow {
  id: string;
  name: string;
  windows_json: unknown;
  holiday_overrides_json: unknown;
  unifi_schedule_id: string | null;
  sync_status: 'pending' | 'synced' | 'failed';
  last_sync_error: string | null;
  last_sync_at: string | null;
  created_at?: string;
  updated_at?: string;
}

function syncStatusToBadge(s: ScheduleRow['sync_status']): SyncState {
  return s === 'synced' ? 'synced' : s === 'pending' ? 'pending' : 'failed';
}

function scheduleUpdateLabel(s: ScheduleRow['sync_status']): string {
  if (s === 'synced') return 'Ready';
  if (s === 'failed') return 'Needs update';
  return 'Update pending';
}

export function ScheduleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState<ScheduleRow | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [outbox, setOutbox] = useState<OutboxJob[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick] = useState(0);
  const { scopeOpts } = useTenantScopedPage();

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setSchedule(null);
    setError(null);
    Promise.all([
      apiClient.get<{ data: ScheduleRow }>(`/api/v1/schedules/${id}`, scopeOpts),
      fetchAuditForResource('schedule', id, 10, scopeOpts),
      fetchOutboxJobsForResource('schedule', id, 10, scopeOpts),
    ])
      .then(([single, a, o]) => {
        if (cancelled) return;
        setSchedule(single.data);
        setAudit(a);
        setOutbox(o);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load schedule');
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadTick, scopeOpts]);

  const recurrenceSummary = useMemo(() => {
    if (!schedule) return '';
    const w = schedule.windows_json;
    if (Array.isArray(w)) return `${w.length} window(s)`;
    return '—';
  }, [schedule]);

  if (error) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate('/schedules')}
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <Icon name="arrow_back" /> Schedules
        </button>
        <div className="p-6 bg-error-container text-on-error-container rounded-lg" role="alert">
          {error}
        </div>
      </div>
    );
  }
  if (!schedule) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  const safeActions = (
    <>
      <Button
        variant="secondary"
        onClick={() => navigate(`/schedules?edit=${encodeURIComponent(schedule.id)}`)}
      >
        <Icon name="edit" className="text-sm" />
        <span className="ml-1">Edit</span>
      </Button>
      {/* UX-014: "Duplicate" navigated to /schedules?duplicate=<id>. Nothing
          read that parameter and no duplicate flow exists anywhere in the
          product — SchedulesPage's modal only knows create / edit / delete.
          The control is removed rather than left promising a feature that was
          never built; duplicating a schedule is tracked as a product gap. */}
    </>
  );

  // TODO(api-gap): there is no count of access groups using this schedule,
  // so we cannot pre-flight the "block delete if in use" check. Surface
  // the dangerous action — the API enforces the constraint server-side
  // and returns 409, which the dangerous-action modal reports.
  const dangerousActions = (
    <DangerousActionButton
      actionLabel="Delete…"
      title={`Delete schedule "${schedule.name}"?`}
      description="Schedule deletion fails if any access group still references it. Detach those groups first."
      testId="schedule-delete"
      onConfirm={async (reason, intentKey) => {
        // UX-013: the key used to be minted here, so retrying inside the same
        // dialog — the common path, since a failure keeps it open — sent a new
        // Idempotency-Key and the backend could not collapse the duplicate.
        const key = intentKey;
        await apiClient
          .withIdempotencyKey(key)
          .delete(`/api/v1/schedules/${schedule.id}`, {
            headers: { 'x-action-reason': reason },
          });
        navigate('/schedules');
      }}
    />
  );

  return (
    <EntityDetailLayout
      title={schedule.name}
      subtitle={recurrenceSummary}
      badges={<SyncStateBadge state={syncStatusToBadge(schedule.sync_status)} />}
      backHref="/schedules"
      backLabel="Schedules"
      safeActions={safeActions}
      dangerousActions={dangerousActions}
      related={<ScheduleRelatedRail scheduleId={schedule.id} />}
      outbox={
        <OutboxJobList
          jobs={outbox}
          viewAllHref={`/outbox?resource_type=schedule&resource_id=${schedule.id}`}
        />
      }
      audit={<AuditFeed entries={audit} label="Recent activity" />}
    >
      <section
        aria-label="Schedule"
        className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-surface-container-lowest border border-outline-variant rounded-xl p-6"
      >
        <KV k="Name" v={schedule.name} />
        <KV k="Update status" v={scheduleUpdateLabel(schedule.sync_status)} />
        <KV
          k="Last update"
          v={schedule.last_sync_at ? new Date(schedule.last_sync_at).toLocaleString() : '—'}
        />
        <KV k="Recurrence" v={recurrenceSummary} />
        {schedule.last_sync_error ? (
          <div className="md:col-span-2">
            <div className="font-label-md text-label-md uppercase text-on-surface-variant mb-1">
              Last update error
            </div>
            <pre className="font-code text-code bg-error-container text-on-error-container rounded p-3 overflow-x-auto">
              {schedule.last_sync_error}
            </pre>
          </div>
        ) : null}
      </section>
    </EntityDetailLayout>
  );
}

function ScheduleRelatedRail({ scheduleId }: { scheduleId: string }) {
  return (
    <section
      aria-label="Related"
      className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-outline-variant">
        <h2 className="font-label-md text-label-md uppercase text-on-surface-variant">Related</h2>
      </div>
      <ul className="divide-y divide-outline-variant">
        <li>
          <Link
            to={`/access-groups?schedule=${encodeURIComponent(scheduleId)}`}
            className="flex items-center gap-3 px-4 py-3 hover:bg-surface-container-low"
          >
            <Icon name="lock_open" className="text-on-surface-variant" />
            <span className="font-body-md text-body-md text-on-surface flex-1">
              Access Zones using this schedule
            </span>
            <Icon name="chevron_right" className="text-on-surface-variant" />
          </Link>
        </li>
      </ul>
    </section>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div>
      <div className="font-label-md text-label-md uppercase text-on-surface-variant mb-1">{k}</div>
      <div className="font-body-md text-body-md text-on-surface">{v}</div>
    </div>
  );
}
